import { ReactElement, useCallback } from "react";

import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { useAppBackgroundEffectsStore } from "app/stores/useAppBackgroundEffectsStore";
import { TabBarItemID, useLayoutStore } from "app/stores/useLayoutStore";
import { routes } from "config/routes";
import { BottomTabBarContainer } from "ui/BottomTabBarContainer";
import { BottomTabBarItem } from "ui/BottomTabBarItem/BottomTabBarItem";
import { ActivitiesIcon, InboundIcon, InventoryIcon, PickingIcon } from "ui/Icons/Icons";

const RESTOCKING_NOTIFICATION_THRESHOLD = 20;

type TabBarLinkProps = {
  id: TabBarItemID;
  icon: ReactElement;
  title: string;
  showNotificationTag?: boolean;
  to: string;
  testId?: string;
};

const bottomTabBarItems: TabBarLinkProps[] = [
  {
    id: "activities",
    title: "Activities",
    testId: "tab-item-activities",
    icon: <ActivitiesIcon />,
    to: routes.activities.root,
  },
  {
    id: "inbound",
    title: "Inbound",
    testId: "tab-item-inbound",
    icon: <InboundIcon />,
    to: routes.inbound.root,
  },
  {
    id: "inventory",
    title: "Inventory",
    testId: "tab-item-inventory",
    icon: <InventoryIcon />,
    to: routes.inventory.root,
  },
  {
    id: "picking",
    title: "Picking",
    testId: "tab-item-picking",
    icon: <PickingIcon />,
    to: routes.pickingV2.root,
  },
];

function TabBarLink({ to, isActive, ...rest }: TabBarLinkProps & { isActive: boolean }) {
  const navigate = useNavigate();

  return (
    <BottomTabBarItem
      {...rest}
      isActive={isActive}
      onClick={() => navigate(to, { replace: true })}
    />
  );
}

export function TabBar() {
  const { withTabBar, highlightedTab } = useLayoutStore(
    useShallow((state) => ({
      withTabBar: state.withTabBar,
      highlightedTab: state.highlightedTab,
    })),
  );

  const [
    hasOpenUrgentChecks,
    publicRestockingItemCount,
    hasNextOrderForPicking,
    hasUnClaimedCancelledOrder,
  ] = useAppBackgroundEffectsStore(
    useShallow((state) => [
      state.hasOpenUrgentChecks,
      state.publicRestockingItemCount,
      !!state.summaryOfNextOrderForPicking,
      state.hasUnClaimedCancelledOrder,
    ]),
  );

  const shouldShowNotificationTagForItem = useCallback(
    (item: TabBarLinkProps) => {
      switch (item.id) {
        case "picking": {
          return hasNextOrderForPicking;
        }
        case "inventory": {
          return (
            hasOpenUrgentChecks || publicRestockingItemCount >= RESTOCKING_NOTIFICATION_THRESHOLD
          );
        }
        case "inbound": {
          return hasUnClaimedCancelledOrder;
        }
        default:
          return undefined;
      }
    },
    [
      hasNextOrderForPicking,
      hasOpenUrgentChecks,
      hasUnClaimedCancelledOrder,
      publicRestockingItemCount,
    ],
  );

  if (!withTabBar) {
    return null;
  }

  return (
    <BottomTabBarContainer>
      {bottomTabBarItems.map((item) => (
        <TabBarLink
          key={item.id}
          {...item}
          showNotificationTag={shouldShowNotificationTagForItem(item)}
          isActive={highlightedTab === item.id}
          data-testid="bottom-bar-notification"
        />
      ))}
    </BottomTabBarContainer>
  );
}
